import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { show_toast } from "utils/helpers";
import { ModalContext } from "context/ModalContext";
import { DashboardContext } from "context/DashboardContext";
import { createDmvicConfig, updateDmvicConfig } from "services/dvmvic";

// ────────────────────────────────────────────────
// Smaller, compact typography version
// ────────────────────────────────────────────────

const FONT_FAMILY = '"Inter", "Roboto", system-ui, sans-serif';

const Container = styled.div`
  background: white;
  border-radius: 10px;
  // box-shadow: 0 3px 16px rgba(0, 0, 0, 0.07);
  padding: 0.25rem 1.25rem;
  margin: 0.75rem auto;
  width: 100%;
  max-width: 1080px;
  font-family: ${FONT_FAMILY};

  @media (min-width: 992px) {
    padding: 1.75rem 1.5rem;
  }
`;

const Title = styled.h3`
  text-align: left;
  font-weight: 600;
  color: #1a73e8;
  margin: 0 0 1.4rem 0;
  font-size: 1.35rem;             // smaller title
  letter-spacing: -0.01em;
`;

const Fieldset = styled.fieldset`
  border: none;
  margin: 0 0 2rem 0;
  padding: 0;
`;

const Legend = styled.legend`
  font-size: 1.05rem;             // smaller section headers
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  padding: 0;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FullWidthRow = styled(FormRow)`
  @media (min-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.4rem;
  color: #374151;
  font-size: 0.86rem;             // noticeably smaller labels
`;

const Input = styled(Field)`
  padding: 0.62rem 0.9rem;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  font-size: 0.86rem;             // smaller input text
  line-height: 1.4;
  transition: all 0.2s ease;
  font-family: ${FONT_FAMILY};

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2.5px rgba(59, 130, 246, 0.18);
  }

  &:disabled {
    background-color: #f3f4f6;
    color: #6b7280;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 0.62rem 0.9rem;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  font-size: 0.86rem;
  line-height: 1.4;
  background-color: white;
  width: 100%;
  transition: all 0.2s ease;
  font-family: ${FONT_FAMILY};

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2.5px rgba(59, 130, 246, 0.18);
  }

  &:disabled {
    background-color: #f3f4f6;
    color: #6b7280;
    cursor: not-allowed;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const CheckboxInput = styled(Field).attrs({ type: "checkbox" })`
  width: 1rem;
  height: 1rem;
  margin: 0;
  accent-color: #3b82f6;
`;

const CheckboxLabel = styled.label`
  margin-bottom: 0;
  font-size: 0.84rem;             // smaller checkbox text
  color: #374151;
  line-height: 1.4;
  user-select: none;
`;

const ErrorText = styled(ErrorMessage)`
  color: #ef4444;
  font-size: 0.75rem;             // very small errors
  margin-top: 0.25rem;
  font-weight: 500;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: start;
  gap: 1rem;
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button`
  padding: 0.55rem 1.8rem;
  font-size: 0.88rem;             // smaller buttons
  font-weight: 500;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1.4;
  font-family: ${FONT_FAMILY};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
  border: 1px solid #9ca3af;
  color: #4b5563;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #6b7280;
  }
`;

const SubmitButton = styled(Button)`
  background: #3b82f6;
  border: none;
  color: white;

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:active:not(:disabled) {
    background: #1d4ed8;
  }
`;

// ────────────────────────────────────────────────
// Component (logic unchanged)
// ────────────────────────────────────────────────
export default function Dmvic_Credential({ underwriters = [], initialData = null, refetchDmvic, onCancel, onSuccess }) {
  const { credentialsmgt, setCredentialsmgt } = useContext(DashboardContext);
  const { modalClose } = useContext(ModalContext);

  const isEditMode = !!initialData?.id;

  const initialValues = useMemo(() => {
    if (isEditMode) {
      return {
        id: initialData.id,
        username: initialData.credentials?.username || "",
        password: "",
        clientId: initialData.credentials?.clientId || "",
        validateBeforeIssuance: initialData.issuanceConfig?.validateBeforeIssuance ?? true,
        autoConfirmRiskChanges: initialData.issuanceConfig?.autoConfirmRiskChanges ?? false,
        defaultPin: initialData.defaultPin || "",
        defaultEmail: initialData.defaultEmail || "notifications@example.com",
        defaultPhone: initialData.defaultPhone || "+254700000000",
      };
    }

    return {
      username: credentialsmgt?.username || "",
      password: credentialsmgt?.password || "",
      clientId: credentialsmgt?.clientId || "",
      validateBeforeIssuance: true,
      autoConfirmRiskChanges: false,
      defaultPin: "",
      defaultEmail: "notifications@example.com",
      defaultPhone: "+254700000000",
    };
  }, [isEditMode, initialData, credentialsmgt]);

  const validationSchema = Yup.object({
    username: Yup.string().required("Username is required"),
    clientId: Yup.string().required("Client ID is required"),
    defaultEmail: Yup.string().email("Invalid email format").required("Default email is required"),
    defaultPhone: Yup.string().required("Default phone is required"),

    ...( !isEditMode && {
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required"),
    }),

    ...( isEditMode && {
      password: Yup.string().min(8, "Password must be at least 8 characters"),
    }),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setCredentialsmgt((prev) => ({ ...prev, loading: true }));

    try {
      const payload = {
        username: values.username,
        clientId: values.clientId,
        validateBeforeIssuance: values.validateBeforeIssuance,
        autoConfirmRiskChanges: values.autoConfirmRiskChanges,
        defaultPin: values.defaultPin || "",
        defaultEmail: values.defaultEmail,
        defaultPhone: values.defaultPhone,
      };

      if (values.password?.trim()) {
        payload.password = values.password;
      }

      if (isEditMode) {
        payload.id = values.id;
        await updateDmvicConfig(payload, initialData.intermediaryDmvicConfigSystemRef);
        refetchDmvic?.();
        show_toast("DMVIC configuration updated successfully!", "success");
        onSuccess?.()
      } else {
        await createDmvicConfig(payload);
        refetchDmvic?.();
          onSuccess?.()
        show_toast("DMVIC configuration created successfully!", "success");
      }

      setCredentialsmgt({
        ...credentialsmgt,
        ...values,
        loading: false,
      });

      resetForm();
      modalClose();
    } catch (err) {
      console.error("DMVIC config operation failed:", err);
      const errorMsg =
        err?.errors?.[0] ||
        err?.message ||
        (isEditMode ? "Failed to update configuration." : "Failed to create configuration.");
      show_toast(errorMsg, "error");
    } finally {
      setCredentialsmgt((prev) => ({ ...prev, loading: false }));
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Title>
        {isEditMode ? "Edit DMVIC Configuration" : "DMVIC Integration Setup"}
      </Title>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form noValidate>
            <Fieldset>
              <Legend>1. Authentication Credentials</Legend>
              <FormRow>
                <FormGroup>
                  <Label>Username</Label>
                  <Input name="username" autoComplete="username" />
                  <ErrorText name="username" component="div" />
                </FormGroup>

                {!isEditMode && (
                  <FormGroup>
                    <Label>Password</Label>
                    <Input
                      name="password"
                      type="password"
                      autoComplete="new-password"
                    />
                    <ErrorText name="password" component="div" />
                  </FormGroup>
                )}

                <FormGroup style={{ gridColumn: "1 / -1" }}>
                  <Label>Client ID</Label>
                  <Input name="clientId" />
                  <ErrorText name="clientId" component="div" />
                </FormGroup>
              </FormRow>
            </Fieldset>

            <Fieldset>
              <Legend>2. Default Issuance Settings</Legend>
              <FullWidthRow>
                <CheckboxWrapper>
                  <CheckboxInput
                    type="checkbox"
                    name="validateBeforeIssuance"
                    id="validateBeforeIssuance"
                  />
                  <CheckboxLabel htmlFor="validateBeforeIssuance">
                    Validate policy before issuance
                  </CheckboxLabel>
                </CheckboxWrapper>

                <CheckboxWrapper>
                  <CheckboxInput
                    type="checkbox"
                    name="autoConfirmRiskChanges"
                    id="autoConfirmRiskChanges"
                  />
                  <CheckboxLabel htmlFor="autoConfirmRiskChanges">
                    Automatically confirm risk changes
                  </CheckboxLabel>
                </CheckboxWrapper>

                <FormGroup>
                  <Label style={{ color: "#6b7280" }}>Default PIN</Label>
                  <Input name="defaultPin" placeholder="Optional" />
                </FormGroup>

                <FormGroup>
                  <Label>Default Notification Email</Label>
                  <Input name="defaultEmail" type="email" />
                  <ErrorText name="defaultEmail" component="div" />
                </FormGroup>

                <FormGroup>
                  <Label>Default Notification Phone</Label>
                  <Input name="defaultPhone" placeholder="+254..." />
                  <ErrorText name="defaultPhone" component="div" />
                </FormGroup>
              </FullWidthRow>
            </Fieldset>

            <ButtonsContainer>
              <CancelButton type="button" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </CancelButton>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>
                      ↻
                    </span>{" "}
                    {isEditMode ? "Updating..." : "Creating..."}
                  </>
                ) : isEditMode ? (
                  "Update Configuration"
                ) : (
                  "Create Configuration"
                )}
              </SubmitButton>
            </ButtonsContainer>
          </Form>
        )}
      </Formik>

      <style jsx global>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
}